import type { ObjectMap } from "@bbj-types/utils";
import { RoundedChip } from "@data-display/RoundedChip";
import { TypoTooltip } from "@data-display/wrapper/DetectOverflow";
import { Badge, type ButtonProps, Checkbox, FormControl, FormControlLabel, Typography } from "@mui/material";
import { MenuPopover } from "@surfaces/MenuPopover";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";

interface IOffersFilterSourceProps<T extends ISettingFilter> {
  label: string | ReactNode;
  value?: T[];
  settings: readonly T[];
  onChange: (settings: T[]) => void;
  // biome-ignore lint/style/useNamingConvention: <explanation>
  ButtonProps?: Omit<ButtonProps, "open" | "ref" | "onClick">;
  updateCountUsingValue?: boolean;
  badgePosition?: "top-right" | "inside";
  filteredValue?: string;
  disabled?: boolean;
  typotooltip?: boolean;
}

export interface ISettingFilter {
  value: string;
  label: string;
  active: boolean;
}

export const SettingFilter = <T extends ISettingFilter>({
  label,
  value,
  settings,
  ButtonProps,
  onChange,
  filteredValue,
  updateCountUsingValue = false,
  badgePosition = "inside",
  typotooltip = false,
  disabled = false,
}: IOffersFilterSourceProps<T>) => {
  const [isDirty, setDirty] = useState(false);
  const [state, setState] = useState<ObjectMap<T>>({});

  const badgeCount = updateCountUsingValue && value ? value.length : Object.keys(state).length;
  const labelWithCount = useMemo(() => {
    if (badgeCount === 0 || badgePosition !== "inside") {
      return label;
    }
    if (typotooltip) {
      return (
        <>
          <TypoTooltip variant="body1SemiBold" noWrap>
            {label}
          </TypoTooltip>
          <RoundedChip color="yellow" label={badgeCount} sx={{ ml: 0.5 }} />
        </>
      );
    }
    return (
      <>
        <Typography variant="body1">{label}</Typography>
        <RoundedChip color="yellow" label={badgeCount} sx={{ ml: 0.5 }} />
      </>
    );
  }, [label, badgeCount, badgePosition, typotooltip]);

  const options = useMemo(() => {
    return settings.filter((v) => v?.active !== false);
  }, [settings]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const handleChange = useCallback((source: T) => {
    setState((state) => {
      if (state[source.value]) {
        const nState = { ...state };
        delete nState[source.value];
        return nState;
      }
      if (filteredValue) {
        if (source.value === filteredValue && Object.keys(state).length > 0) {
          return { [source.value]: source };
        }
        const nState = { ...state };
        delete nState[filteredValue];
        return { ...nState, [source.value]: source };
      }
      return { ...state, [source.value]: source };
    });
    setDirty(true);
  }, []);

  const handleClose = useCallback(() => {
    if (isDirty) {
      onChange(Object.values(state));
      setDirty(false);
    }
  }, [isDirty, state, onChange]);

  useEffect(() => {
    if (value) {
      setState(
        value.reduce<ObjectMap<T>>((obj, setting) => {
          obj[setting.value] = setting;
          return obj;
        }, {})
      );
    }
  }, [value]);

  const menuPopover = (
    <MenuPopover
      disabled={disabled}
      label={typeof label === "string" ? labelWithCount : label}
      onClose={handleClose}
      ButtonProps={ButtonProps}
    >
      <FormControl size="small">
        {options.map((source) => (
          <FormControlLabel
            key={source.value}
            control={
              <Checkbox
                checked={Boolean(state[source.value])}
                onChange={() => handleChange(source)}
                sx={{ mr: 0.5, p: 0.5 }}
                size="small"
              />
            }
            label={source.label}
            sx={{ m: 0, pr: 0.5 }}
          />
        ))}
      </FormControl>
    </MenuPopover>
  );

  switch (badgePosition) {
    case "top-right":
      return (
        <Badge badgeContent={badgeCount} color="yellow">
          {menuPopover}
        </Badge>
      );
    default:
      return menuPopover;
  }
};
